import { useCallback, useEffect, useRef, useState } from "react";

import { AlertTriangle, CheckCircle2, XCircle } from "lucide-react";
import { useDispatch } from "react-redux";

import { SaveFileRequest, TaskResultResponse } from "@/api/generated/types.gen";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { DocumentCategory } from "@/constants/documents";
import { useSaveClassifiedFiles } from "@/hooks/mutations/use-bundle-splitter";
import { useTaskResult } from "@/hooks/queries/use-bundle-splitter";
import { mapToValidClassification } from "@/pages/ReportStudio/DocumentSorter/utils/classificationMapper";
import { getFriendlyErrorMessage, notifyError } from "@/utils/notification";

import { FilePreview } from "../FilePreview";
import { ManualClassificationDialog } from "./ManualClassification";

type ManualClassificationFile = {
  filename: string;
  classification: string;
  fileKey?: string;
  url?: string;
  case?: { case_number: string };
  fileId?: string;
  startPage?: number;
  endPage?: number;
};

type ResultPhaseProps = {
  file: File;
  taskId: string;
  caseNumber: string;
  onToastClose?: () => void;
  onComplete: (needsReview: boolean) => void;
  onDismiss?: () => void;
};

export const ResultPhase = ({
  file,
  taskId,
  caseNumber,
  onToastClose,
  onComplete,
  onDismiss,
}: ResultPhaseProps) => {
  const [filesForManualClassification, setFilesForManualClassification] = useState<
    ManualClassificationFile[]
  >([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isSaveComplete, setIsSaveComplete] = useState(false);
  const [isSaveFailed, setIsSaveFailed] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { data: taskResult } = useTaskResult({
    task_id: taskId,
    enabled: true,
  });

  const { mutateAsync: saveClassifiedFiles } = useSaveClassifiedFiles();
  const hasSavedRef = useRef(false);
  const dispatch = useDispatch();

  const handleSaveFiles = useCallback(
    async (result: TaskResultResponse) => {
      if (!result.result || !result.result.split_blob_urls) {
        setErrorMessage("No split files found in the task result.");
        return;
      }

      try {
        const rangeMap = new Map<string, { startPage: number; endPage: number }>();

        const files: SaveFileRequest[] = result.result.split_blob_urls.map(
          (item) => {
            const classification = mapToValidClassification(item.class);
            const filenameFromUrl =
              item.url.split("/").pop() ||
              `${item.class.replaceAll(" ", "_")}_${item.startPage}-${item.endPage}.pdf`;

            rangeMap.set(filenameFromUrl, {
              startPage: item.startPage,
              endPage: item.endPage,
            });

            return {
              classification,
              file_url: item.url,
              filename: filenameFromUrl,
              filenameDefault: result.result?.file,
            };
          }
        );

        setErrorMessage(null);
        const data = await saveClassifiedFiles({
          case_number: caseNumber,
          data: { files },
        });

        let needsReview = false;
        if (data?.saved_files) {
          const manualClassificationFiles = data.saved_files
            .filter(
              (file) =>
                file.classification ===
                DocumentCategory.GENERAL_CORRESPONDENCE_AND_MISCELLANEOUS_DOCUMENTS
            )
            .map((file) => {
              const filename = String(file.filename || "");
              const range = rangeMap.get(filename);
              return {
                filename,
                name: filename,
                classification: String(file.classification || ""),
                fileKey:
                  typeof file.blob_path === "string" ? file.blob_path : undefined,
                fileId:
                  typeof file.file_id === "string"
                    ? file.file_id
                    : String(file.file_id),
                case: { case_number: caseNumber },
                startPage: range?.startPage,
                endPage: range?.endPage,
              };
            });
          setFilesForManualClassification(manualClassificationFiles);
          needsReview = manualClassificationFiles.length > 0;
        }

        setIsSaveComplete(true);
        setIsSaveFailed(false);
        onComplete(needsReview);
      } catch (error) {
        console.error("Error saving files:", error);
        notifyError(error, dispatch);
        setErrorMessage(getFriendlyErrorMessage(error));
        setIsSaveFailed(true);
      }
    },
    [caseNumber, saveClassifiedFiles, dispatch, onComplete]
  );

  useEffect(() => {
    if (taskResult && !hasSavedRef.current) {
      hasSavedRef.current = true;
      handleSaveFiles(taskResult);
    }
  }, [taskResult, handleSaveFiles]);

  const isFetching = !taskResult && !errorMessage;

  return (
    <>
      <FilePreview
        file={file}
        isSuccess={isSaveComplete && !isSaveFailed}
        isFailed={isSaveFailed}
        isLoading={!isSaveComplete && !isSaveFailed}
        onClear={isSaveComplete ? onDismiss : undefined}
      />

      {errorMessage && (
        <Alert variant="destructive" className="mb-3">
          <XCircle />
          <AlertDescription>{errorMessage}</AlertDescription>
        </Alert>
      )}

      {isSaveComplete ? (
        filesForManualClassification.length > 0 ? (
          <div className="w-full space-y-4">
            <Alert variant="warning" className="max-w-lg">
              <AlertTriangle className="mt-0.5 text-warning" />
              <AlertDescription>
                We have mapped most of your documents. But{" "}
                {filesForManualClassification.length}{" "}
                {filesForManualClassification.length === 1
                  ? "document needs"
                  : "documents need"}{" "}
                classification. Please review before continuing.
              </AlertDescription>
            </Alert>

            <Button
              variant="default"
              className="mt-3 w-full"
              onClick={() => setIsDialogOpen(true)}
            >
              Review Now
            </Button>

            <ManualClassificationDialog
              files={filesForManualClassification}
              open={isDialogOpen}
              onOpenChange={setIsDialogOpen}
              onFileClassified={(fileId) => {
                setFilesForManualClassification((prevFiles) =>
                  prevFiles.filter((file) => file.fileId !== fileId)
                );
              }}
              onToastClose={onToastClose}
              caseNumber={caseNumber}
            />
          </div>
        ) : (
          <Alert variant="success">
            <CheckCircle2 />
            <AlertDescription>Files saved successfully</AlertDescription>
          </Alert>
        )
      ) : errorMessage && taskResult ? (
        <Button
          onClick={() => {
            hasSavedRef.current = false;
            handleSaveFiles(taskResult);
          }}
        >
          Retry Save
        </Button>
      ) : (
        isFetching && (
          <div className="space-y-1.5">
            <Progress className="h-2" />
            <p className="text-center text-xs text-foreground-muted">
              Loading Results
            </p>
          </div>
        )
      )}
    </>
  );
};
